import React, { useCallback, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import FormatCurrency from 'components/Common/FormatCurrency'

interface Props {
  flightsAvailable: boolean
  flights: boolean
  isMemberPrice?: boolean
  hotelTotalPrice: number
  hotelFlightTotalPrice?: number
  pricesLoading?: boolean
}

function BaseHotelFlightsToggleButton(props: Props) {
  const { flightsAvailable, flights, isMemberPrice, hotelTotalPrice, hotelFlightTotalPrice, pricesLoading } = props
  const dispatch = useOfferPageDispatch()
  const intl = useIntl()

  const noFlightsPrice = hotelTotalPrice === hotelFlightTotalPrice
  const disableFlights = !flightsAvailable || noFlightsPrice

  const handleOptionChange = useCallback((value: string) => {
    const isFlightsSelected = value === 'flights'
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected })
  }, [dispatch])

  useEffect(() => {
    if (!disableFlights && flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableFlights])

  const flightsSubtitle = (() => {
    if (pricesLoading) return undefined
    if (flightsAvailable && hotelFlightTotalPrice) {
      const price = <FormatCurrency format="roundedDollar" value={hotelFlightTotalPrice} />
      if (isMemberPrice) {
        return <FormattedMessage
          id="VD2fb8THas"
          defaultMessage="From {price}"
          description="Member price with From prefix for hotel flights toggle option"
          values={{ price }}
        />
      }
      return price
    }
    if (!flightsAvailable) {
      return intl.formatMessage({
        id: 'gve3LglgVc',
        defaultMessage: 'Unavailable for your dates',
        description: 'Message shown when flights option is unavailable for selected dates',
      })
    }
  })()

  return (
    <SegmentedOptionsGroup inputsName="flights-toggle" onValueChange={handleOptionChange}>
      <SegmentedOption
        value="hotel"
        title={intl.formatMessage({
          id: 'b0pv8cJW9S',
          defaultMessage: 'Hotel',
          description: 'Label for hotel-only option in hotel/flights toggle button',
        })}
        subtitle={(() => {
          if (pricesLoading || !hotelTotalPrice) return undefined
          const price = <FormatCurrency format="roundedDollar" value={hotelTotalPrice} />
          if (isMemberPrice) {
            return <FormattedMessage
              id="MzS3cJt4fJ"
              defaultMessage="From {price}"
              description="Member price with From prefix for hotel-only toggle option"
              values={{ price }}
            />
          }
          return price
        })()}
        checked={!flights}
      />
      <SegmentedOption
        value="flights"
        title={intl.formatMessage({
          id: '2NPAn/4P53',
          defaultMessage: 'Hotel + flights',
          description: 'Label for hotel with flights option in hotel/flights toggle button',
        })}
        subtitle={flightsSubtitle}
        icon={<LinePlaneDepartureIcon />}
        checked={flights}
        disabled={disableFlights}
      />
    </SegmentedOptionsGroup>
  )
}

export default withOfferPageAndReduxState((state) => ({
  flights: state.flights,
}), BaseHotelFlightsToggleButton)
